import { Form } from '@/services/form';
import { API_URLS } from '@/api/pumba/urls';
import router from '@/router';
import Store from '../index';
import userApi from '@/api/pumba/userApi';
import developerApi from '@/api/app-store/developerApi';
import { useMixpanel } from '@/composables/useMixpanel';

const { resetUser } = useMixpanel();

export const state = {
    form: new Form({
        email: '',
        password: '',
    }),
    user: null,
    isAuthenticated: false,
    isYampiEmail: null,
    loading: false,
    signatures: {},
};

export const getters = {
    form: (state) => state.form,
    user: (state) => state.user,
    userId: (state) => state.user?.data?.id,
    loading: (state) => state.loading,
    hasEmailConfirmation: (state) => !!state?.user?.data?.email_verified_at,
    acceptedTerms: (state) => !state.user?.data?.accepted_terms,
    userPasswordChanged: (state) => !!state?.user?.data?.password_changed,
    displayFirstName: (state) => state?.user?.data?.name.split(' ')[0],
    displayFirstSocialName: (state) => (state?.user?.data?.social_name ? state.user.data.social_name.split(' ')[0] : null),
    $auth_intercom_signature: (state) => state.signatures.intercom,
    isAuthenticated: (state) => !!state.isAuthenticated,
};

export const mutations = {
    ['UPDATE_FORM'](state, { index, value }) {
        state.form[index] = value;
    },

    ['FETCH_USER_SUCCESS'](states, { user }) {
        states.user = user;
    },

    ['LOGOUT'](states) {
        states.isAuthenticated = false;
        states.user = null;
        if (window.Intercom) {
            window.Intercom('shutdown');
        }
    },

    ['RESET_FORM'](states) {
        states.form.reset();
    },

    ['UPDATE_LOADING_STATUS'](states, status) {
        states.loading = status;
    },

    ['SUCCESS_INTERCOM_SIGNATURE'](states, value) {
        states.signatures.intercom = value;
    },

    ['UPDATE_USER_CONFIRMATION'](state, value) {
        state.user.data.email_verified_at = value ? new Date().toISOString() : null;
    },

    SET_AUTHENTICATED(state, value) {
        state.isAuthenticated = value;
    },

    UPDATE_ACCEPTED_TERMS(state, value) {
        state.user.data.accepted_terms = value;
    },

    SET_YAMPI_EMAIL(state, value) {
        state.isYampiEmail = value;
    },
};

export const actions = {
    async checkAuth({ commit }) {
        try {
            const { data: user } = await userApi.fetchMe();

            commit('FETCH_USER_SUCCESS', { user });
            commit('SET_AUTHENTICATED', true);
            Store.dispatch('profile/updateUserCodes', user.data.partner_profiles);
        } catch (error) {
            commit('LOGOUT');
            throw error;
        }
    },

    async fetchUser({ commit, dispatch }) {
        try {
            const { data: user } = await userApi.fetchMe();

            commit('FETCH_USER_SUCCESS', { user });

            Store.dispatch('profile/updateUserCodes', user.data.partner_profiles);
        } catch (error) {
            dispatch('logout');
            throw error;
        }
    },

    fetchUtils() {
        return userApi.fetchUtils();
    },

    async getIntercomSignature({ dispatch, commit }) {
        const { data: intercomSignature } = await dispatch('fetchUtils');

        commit('SUCCESS_INTERCOM_SIGNATURE', intercomSignature.intercom_signature);
    },

    async logout({ commit }, force) {
        resetUser();

        if (force) {
            commit('LOGOUT');
            router.push({ name: 'login' });
            return;
        }

        await userApi.logout();
        commit('LOGOUT');
        router.push({ name: 'login' });
    },

    async submit({ state: states, commit, dispatch }) {
        try {
            commit('UPDATE_LOADING_STATUS', true);

            await states.form.post(API_URLS.AUTH.LOGIN);

            await dispatch('checkAuth');

            Store.dispatch('global_loading/changeLoadingStatus', true);

            commit('RESET_FORM');
        } finally {
            commit('UPDATE_LOADING_STATUS', false);
        }
    },

    async checkYampiEmail({ commit }) {
        const data = await developerApi.fetchMe();
        commit('SET_YAMPI_EMAIL', data.is_yampi_email);
    },

    updateAuthForm({ commit }, { index, value }) {
        commit('UPDATE_FORM', { index, value });
    },

    updateUserConfirmation({ commit }, value) {
        commit('UPDATE_USER_CONFIRMATION', value);
    },

    updateAcceptedTerms({ commit, dispatch }, value) {
        commit('UPDATE_ACCEPTED_TERMS', value);
        dispatch('fetchUser');
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};
